import { Dispatch } from 'redux';
import {
  HOME_SET_WIDGET_PREFERENCES,
  HOME_UPDATE_WIDGET_PREFERENCE,
  HOME_RECORD_QUICK_LINK_CLICK,
} from '@core/constants/actions';
import * as api from '@core/api/homeScreen/homeScreenPreferences';
import * as quickLinksApi from '@core/api/homeScreen/homeQuickLinks';
import showToast from '@core/toasts/operations/showToast.operation';
import widgetsLibrary from '@app/components/presentational/Home/widgetsLibrary';
import { analyticsTrackEvent } from '@core/utils/analytics';
import {
  HOME_WIDGET_HIDDEN,
  HOME_WIDGET_UNHIDDEN,
  HOME_WIDGET_ITEM_TYPE,
  NAVIGATION_SOURCE_STARRED_SPACES,
  NAVIGATION_SOURCE_RECENTLY_VIEWED_SPACES,
  NAVIGATION_SOURCE_UPCOMING_ASSIGNMENTS,
  NAVIGATION_SOURCE_MESSAGES,
  NAVIGATION_SOURCE_QUICK_LINKS,
  NAVIGATION_SOURCE_OUTLOOK,
  WIDGET_ID_OUTLOOK,
} from '@core/constants/analytics';

const getNavigationSourceForWidget = (widgetId: string) => {
  switch (widgetId) {
    case 'home-starred-spaces-widget':
      return NAVIGATION_SOURCE_STARRED_SPACES;
    case 'home-recently-viewed-spaces-widget':
      return NAVIGATION_SOURCE_RECENTLY_VIEWED_SPACES;
    case 'home-upcoming-assignments-widget':
      return NAVIGATION_SOURCE_UPCOMING_ASSIGNMENTS;
    case 'home-messages-widget':
      return NAVIGATION_SOURCE_MESSAGES;
    case 'home-quick-links-widget':
      return NAVIGATION_SOURCE_QUICK_LINKS;
    case WIDGET_ID_OUTLOOK:
      return NAVIGATION_SOURCE_OUTLOOK;
    default:
      return null;
  }
};
export const fetchWidgetPreferences = () => async (dispatch: Dispatch) => {
  try {
    const response = await api.fetchWidgetPreferences();
    const apiPreferences = response.homeUserPreferences || [];

    // Ensure every widget in widgetsLibrary has a preference entry
    const mergedPreferences = widgetsLibrary.map((widget) => {
      const existing = apiPreferences.find(
        (p: any) => p.widgetId === widget.id
      );
      return (
        existing || {
          widgetId: widget.id,
          isHidden: false,
        }
      );
    });

    dispatch({
      type: HOME_SET_WIDGET_PREFERENCES,
      preferences: mergedPreferences,
    });
  } catch (error) {
    console.error('Failed to fetch widget preferences', error);
  }
};

export const toggleWidgetPreference =
  (widgetId: string, isHidden: boolean) => async (dispatch: Dispatch) => {
    // Optimistic UI: update local state immediately, persist in background.
    dispatch({
      type: HOME_UPDATE_WIDGET_PREFERENCE,
      widgetId,
      isHidden,
    });

    const navigationSource = getNavigationSourceForWidget(widgetId);
    if (navigationSource) {
      analyticsTrackEvent(
        isHidden ? HOME_WIDGET_HIDDEN : HOME_WIDGET_UNHIDDEN,
        {
          itemType: HOME_WIDGET_ITEM_TYPE,
          navigationSource: navigationSource,
        }
      );
    }

    try {
      await api.patchWidgetPreference(widgetId, isHidden);
    } catch (error) {
      console.error('Failed to update widget preference', error);
      dispatch({
        type: HOME_UPDATE_WIDGET_PREFERENCE,
        widgetId,
        isHidden: !isHidden,
      });
      // Use any as Dispatch doesn't natively support thunks in the standard redux typing without extra configuration
      dispatch(
        showToast({
          message: 'Failed to update widget visibility',
          variant: 'error',
        }) as any
      );
    }
  };

export const recordQuickLinkClick =
  (linkKey: string, role: string) => async (dispatch: Dispatch) => {
    dispatch({
      type: HOME_RECORD_QUICK_LINK_CLICK,
      linkKey,
      role,
    });

    try {
      await quickLinksApi.patchQuickLinkClick(linkKey, role);
    } catch (error) {
      console.error('Failed to record quick link click', error);
    }
  };
